import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { useI18n } from "../i18n";
import { pathFor, OFFICIAL_LINKS } from "../config/site";

const Ext = ({ href, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="focus-gold inline-flex items-center gap-1.5 text-jlo-warm/75 hover:text-jlo-gold transition-colors text-sm"
  >
    {label}
    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
  </a>
);

export default function Footer({ pageKey }) {
  const { lang, d } = useI18n();
  const exploreLinks = ["timeline", "music", "film", "style", "legacy"];
  const infoLinks = ["about", "disclaimer", "contact", "privacy"];

  return (
    <footer data-testid="site-footer" className="relative z-10 border-t border-jlo-gold/15 bg-jlo-bg2 mt-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="font-display text-2xl font-bold">
              <span className="gold-text">JLo</span>
              <span className="text-jlo-warm"> Timeline</span>
            </div>
            <p className="mt-4 text-sm text-jlo-gray leading-relaxed">{d.footer.tagline}</p>
            <p className="mt-4 text-xs text-jlo-gold/80 font-semibold uppercase tracking-wider">
              {d.footer.notOfficial}
            </p>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-jlo-gold font-bold mb-4">{d.footer.explore}</h3>
            <ul className="space-y-2.5">
              {exploreLinks.map((k) => (
                <li key={k}>
                  <Link to={pathFor(lang, k)} data-testid={`footer-${k}`} className="text-sm text-jlo-warm/75 hover:text-jlo-gold transition-colors">
                    {d.ui.nav[k]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-jlo-gold font-bold mb-4">{d.footer.official}</h3>
            <ul className="space-y-2.5">
              <li><Ext href={OFFICIAL_LINKS.website} label={d.ui.officialWebsite} /></li>
              <li><Ext href={OFFICIAL_LINKS.store} label={d.ui.officialStore} /></li>
              <li><Ext href={OFFICIAL_LINKS.onthejlo} label={d.ui.onTheJlo} /></li>
            </ul>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-jlo-gold font-bold mb-4">{d.footer.legal}</h3>
            <ul className="space-y-2.5">
              {infoLinks.map((k) => (
                <li key={k}>
                  <Link to={pathFor(lang, k)} data-testid={`footer-${k}`} className="text-sm text-jlo-warm/75 hover:text-jlo-gold transition-colors">
                    {d.ui.nav[k]}
                  </Link>
                </li>
              ))}
              <li>
                <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" className="text-sm text-jlo-warm/75 hover:text-jlo-gold transition-colors">Sitemap</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-jlo-gold/10">
          <p className="text-xs text-jlo-gray leading-relaxed max-w-3xl">{d.disclaimer.long}</p>
          <p className="mt-4 text-xs text-jlo-gray/80">
            © {new Date().getFullYear()} {d.footer.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
}
